import { and, asc, eq, inArray } from 'drizzle-orm'

import type { Database } from '../client'
import type {
  EarnedXpDaily,
  LessonHistoryDaily,
  LessonMetricsDaily,
  LessonSubjectMetricsDaily,
  ReadingHistoryDaily,
  ReadingJournal,
  ReadingMetricsDaily,
} from '../schema'
import type { DatabaseTransaction } from './child-merge'
import { member, organization, team, teamMember, user } from '../schema'
import { IS_SCHOOL_ORG } from './home-connect'

/**
 * Either handle works: the pooled/http `db` or an open `dbPool.transaction(...)` handle. The
 * producers that call this run inside a transaction, where a `Database`-typed parameter would still
 * compile but execute on a different connection — outside the transaction, so it would read rows the
 * transaction has not committed yet (and would not be rolled back with it).
 */
export type DatabaseOrTransaction = Database | DatabaseTransaction

/**
 * The id-shaped snapshot columns. All seven per-user lookup tables carry these three.
 *
 * NONE of them is a foreign key. A snapshot must carry no referential dependency at all: with
 * `ON DELETE SET NULL` the database would erase the ids the moment a school or a class is deleted,
 * which is precisely the history loss the snapshot exists to prevent. They are plain text.
 */
export interface MetricSnapshotIds {
  /** The id of the organization whose `type` array contains `'school'`. Not an FK. */
  schoolOrganizationId: string | null
  /** The id of the `team` ("Kelas" / rombel) inside that school organization. Not an FK. */
  schoolTeamId: string | null
  /** The "Tingkat" / grade, copied from `user.schoolClass`. Already human-readable — no label. */
  schoolClass: string | null
}

/**
 * The human-readable labels that go beside the ids.
 *
 * Without a foreign key an id alone is unrenderable once the row it pointed at is gone, so the
 * label has to be stored next to it. Only the four low-cardinality tables carry these — see
 * {@link LabelledSnapshotTable}.
 */
export interface MetricSnapshotLabels {
  /** `user.name` at roll-up time — a learner can be renamed or deleted. */
  studentName: string | null
  /** `user.image` at roll-up time (the avatar URL). */
  studentImage: string | null
  /** `organization.name` at roll-up time. */
  schoolOrganizationName: string | null
  /** `team.name` at roll-up time — the class label a report prints, e.g. "1 A". */
  schoolTeamName: string | null
  /** `team.image` at roll-up time (the class avatar URL). */
  schoolTeamImage: string | null
}

/**
 * The school context stamped onto a per-user lookup row at roll-up time.
 *
 * Every field is nullable: a child who belongs only to a family organization has no school context,
 * and a child in a school with no rombel yet has an organization but no team.
 */
export interface MetricSnapshotContext extends MetricSnapshotIds, MetricSnapshotLabels {
  userId: string
}

/**
 * The lookup tables that carry ids **and** labels: the (user, day)-grain tables a "Laporan"
 * aggregates from, plus the (user, week, dayOfWeek) journal.
 */
export type LabelledSnapshotTable =
  | typeof LessonMetricsDaily
  | typeof LessonSubjectMetricsDaily
  | typeof ReadingJournal
  | typeof ReadingMetricsDaily

/**
 * The lookup tables that carry the ids only.
 *
 * Their grain multiplies the (user, day) grain by a book, an enrollment or a skill, so they are the
 * biggest tables here. Repeating a name and two URLs on every one of those rows would buy nothing:
 * a report aggregates from the (user, day) tables above, and the ids kept here are still enough to
 * filter by school or class and to join back to the labelled rows.
 */
export type IdOnlySnapshotTable = typeof EarnedXpDaily | typeof LessonHistoryDaily | typeof ReadingHistoryDaily

export type SnapshotTable = IdOnlySnapshotTable | LabelledSnapshotTable

/**
 * Chunk size for the id list. The query below binds each id three times (school-org subquery,
 * school-team subquery, outer `user` filter), so 2000 ids cost 6000 bind parameters — comfortably
 * under Postgres' 65535 limit. Chunking is not an N+1: the whole batch still resolves in
 * `ceil(n / 2000)` round trips, independent of how many users have a school.
 */
const CHUNK_SIZE = 2000

/**
 * Resolves the membership snapshot for a batch of learners in one query.
 *
 * Producers call this ONCE per run, for the set of users they are about to write, and stamp the
 * result onto every row. That is what lets a future "Laporan" group by the class/school a child was
 * in when the metric was produced instead of where they sit today.
 *
 * The labels (student name/avatar, school name, class name/avatar) come back on the same query as
 * the ids — the subqueries that pick the organization and the team already join `organization` and
 * `team`, so reading their name costs no extra round trip. Whether a producer may stamp them is
 * decided by {@link snapshotColumnsFor}, not here.
 *
 * Determinism, and its limits:
 * - A child can be a member of several organizations at once (family + school, and after a
 *   Home-School Connect, potentially more than one school). We take the `member` row in a
 *   `'school'`-typed organization with the earliest `member.createdAt`, tie-broken on
 *   `member.organizationId`, so repeated runs agree.
 * - `team_member` has NO unique on `(teamId, userId)` and a child may sit in several teams of the
 *   same school. We take the earliest `team_member.createdAt`, tie-broken on `team_member.teamId`.
 *   `team_member.createdAt` is nullable; Postgres sorts NULLs last under `ASC`, so rows without a
 *   timestamp lose to rows with one and then fall back to the id. A child genuinely in two classes
 *   is therefore reported in exactly one of them — the model has no notion of a primary class.
 *
 * @returns a map keyed by user id, containing an entry for EVERY id passed in. Ids that do not exist
 *   in `user`, or that have no school membership, map to an all-null context rather than being
 *   absent, so callers never have to distinguish "unknown" from "no school".
 */
export async function resolveMetricSnapshotContexts(
  db: DatabaseOrTransaction,
  userIds: readonly string[],
): Promise<Map<string, MetricSnapshotContext>> {
  const contexts = new Map<string, MetricSnapshotContext>()

  const ids = [...new Set(userIds)].filter((id) => id.length > 0)
  if (ids.length === 0) {
    return contexts
  }

  for (const id of ids) {
    contexts.set(id, { userId: id, ...EMPTY_METRIC_SNAPSHOT_CONTEXT })
  }

  for (let offset = 0; offset < ids.length; offset += CHUNK_SIZE) {
    const chunk = ids.slice(offset, offset + CHUNK_SIZE)

    // One school organization per user: earliest membership wins. The organization NAME is carried
    // out of this subquery rather than joined again outside it, so the label is guaranteed to be
    // the name of the very organization the id points at.
    const schoolOrg = db
      .selectDistinctOn([member.userId], {
        userId: member.userId,
        organizationId: member.organizationId,
        organizationName: organization.name,
      })
      .from(member)
      .innerJoin(organization, eq(organization.id, member.organizationId))
      .where(and(inArray(member.userId, chunk), IS_SCHOOL_ORG))
      .orderBy(member.userId, asc(member.createdAt), asc(member.organizationId))
      .as('school_org')

    // One team per (user, organization): earliest team membership wins. Distinct on the organization
    // too, so the outer join can pick the team that belongs to the school organization chosen above
    // rather than whichever team happened to sort first across all of the child's organizations.
    const schoolTeam = db
      .selectDistinctOn([teamMember.userId, team.organizationId], {
        userId: teamMember.userId,
        organizationId: team.organizationId,
        teamId: teamMember.teamId,
        teamName: team.name,
        teamImage: team.image,
      })
      .from(teamMember)
      .innerJoin(team, eq(team.id, teamMember.teamId))
      .where(inArray(teamMember.userId, chunk))
      .orderBy(teamMember.userId, team.organizationId, asc(teamMember.createdAt), asc(teamMember.teamId))
      .as('school_team')

    const rows = await db
      .select({
        userId: user.id,
        studentName: user.name,
        studentImage: user.image,
        schoolClass: user.schoolClass,
        schoolOrganizationId: schoolOrg.organizationId,
        schoolOrganizationName: schoolOrg.organizationName,
        schoolTeamId: schoolTeam.teamId,
        schoolTeamName: schoolTeam.teamName,
        schoolTeamImage: schoolTeam.teamImage,
      })
      .from(user)
      .leftJoin(schoolOrg, eq(schoolOrg.userId, user.id))
      .leftJoin(
        schoolTeam,
        and(eq(schoolTeam.userId, user.id), eq(schoolTeam.organizationId, schoolOrg.organizationId)),
      )
      .where(inArray(user.id, chunk))

    for (const row of rows) {
      contexts.set(row.userId, {
        userId: row.userId,
        // `user.name` is NOT NULL, but a name that is the empty string is not a label — normalize it
        // to null so a reader renders its own fallback instead of a blank.
        studentName: row.studentName.length > 0 ? row.studentName : null,
        studentImage: row.studentImage ?? null,
        schoolOrganizationId: row.schoolOrganizationId ?? null,
        schoolOrganizationName: row.schoolOrganizationName ?? null,
        // A team without a school organization would be a class in a family organization — not a
        // thing the product creates, but the join above already guarantees it cannot happen.
        schoolTeamId: row.schoolTeamId ?? null,
        schoolTeamName: row.schoolTeamName ?? null,
        schoolTeamImage: row.schoolTeamImage ?? null,
        schoolClass: row.schoolClass ?? null,
      })
    }
  }

  return contexts
}

/** The all-null context, for a user the resolver had nothing to say about. */
export const EMPTY_METRIC_SNAPSHOT_CONTEXT: MetricSnapshotIds & MetricSnapshotLabels = {
  schoolOrganizationId: null,
  schoolOrganizationName: null,
  schoolTeamId: null,
  schoolTeamName: null,
  schoolTeamImage: null,
  schoolClass: null,
  studentName: null,
  studentImage: null,
}

/**
 * The snapshot columns for one user, ready to spread into a lookup-table insert row.
 *
 * The TABLE decides the shape, which is the point: pass a labelled table and you get the labels,
 * pass an id-only table and the labels are not in the returned type at all, so there is no way to
 * write columns the table does not have — and no way to forget the ones it does. The overloads are
 * the gate; the `in` check below only mirrors them at runtime.
 *
 * Falls back to all-null when the user was not in the resolved batch, so a producer can never
 * silently drop a row for a missing context.
 */
export function snapshotColumnsFor(
  table: LabelledSnapshotTable,
  contexts: ReadonlyMap<string, MetricSnapshotContext>,
  userId: string,
): MetricSnapshotIds & MetricSnapshotLabels
export function snapshotColumnsFor(
  table: IdOnlySnapshotTable,
  contexts: ReadonlyMap<string, MetricSnapshotContext>,
  userId: string,
): MetricSnapshotIds
export function snapshotColumnsFor(
  table: SnapshotTable,
  contexts: ReadonlyMap<string, MetricSnapshotContext>,
  userId: string,
): MetricSnapshotIds | (MetricSnapshotIds & MetricSnapshotLabels) {
  const context = contexts.get(userId)

  const ids: MetricSnapshotIds = {
    schoolOrganizationId: context?.schoolOrganizationId ?? null,
    schoolTeamId: context?.schoolTeamId ?? null,
    schoolClass: context?.schoolClass ?? null,
  }

  // Derived from the table's own column set, so a table that gains or loses the label columns
  // cannot drift away from what is stamped into it.
  if (!('studentName' in table)) {
    return ids
  }

  return {
    ...ids,
    studentName: context?.studentName ?? null,
    studentImage: context?.studentImage ?? null,
    schoolOrganizationName: context?.schoolOrganizationName ?? null,
    schoolTeamName: context?.schoolTeamName ?? null,
    schoolTeamImage: context?.schoolTeamImage ?? null,
  }
}
